import React from 'react';

/**
 * Textarea Component
 *
 * A reusable multi-line text input styled to match the Input component
 * (shares the `input` / `input-error` classes from the Zamanat design system).
 *
 * @param {Object} props
 * @param {string} props.label - Textarea label
 * @param {boolean} props.required - Whether the field is required
 * @param {string} props.error - Error message
 * @param {string} props.helperText - Helper text shown below the field
 * @param {number} props.rows - Visible number of text rows (default 4)
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.className - Additional CSS classes
 */
const Textarea = React.forwardRef(({
  label,
  required = false,
  error,
  helperText,
  rows = 4,
  placeholder,
  className = '',
  ...props
}, ref) => {
  const textareaClasses = [
    'input',
    error && 'input-error',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className="w-full">
      {label && (
        <label className={`label ${required ? 'label-required' : ''}`}>
          {label}
        </label>
      )}
      <textarea
        ref={ref}
        rows={rows}
        placeholder={placeholder}
        className={textareaClasses}
        {...props}
      />
      {error ? (
        <p className="mt-2 text-sm text-red-600">{error}</p>
      ) : helperText ? (
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{helperText}</p>
      ) : null}
    </div>
  );
});

Textarea.displayName = 'Textarea';

export default Textarea;
